import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';

/**
 * Hook to detect if the current company is in test mode
 * 
 * Test mode is determined by the presence of specific test API keys:
 * - NMI tokenizationKey: T3qmAr-ed6Yz9-DC3tCN-Y7nGfy
 * - NMI securityKey: JvMr9YHy6QDA2v2pjSJT9dA2r3u638xz
 * - Sendgrid API Key: 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
 * 
 * When in test mode, a red banner will be displayed at the top of the page
 * indicating that the account is in test mode and funds will not be moved.
 * 
 * @returns {Object} Object containing isTestMode and isLoading states
 */
export const useTestMode = () => {
  const { user } = useAuth();
  const [isTestMode, setIsTestMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkTestMode = async () => {
      if (!user?.companyId) return;
      
      setIsLoading(true);
      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.companyId));
        if (!companyDoc.exists()) return;
        
        const companyData = companyDoc.data();
        
        // Test API keys that indicate test mode
        const testTokenizationKey = 'T3qmAr-ed6Yz9-DC3tCN-Y7nGfy';
        const testSecurityKey = 'JvMr9YHy6QDA2v2pjSJT9dA2r3u638xz';

        const hasTestTokenizationKey = companyData?.wikiPayitSettings?.tokenizationKey === testTokenizationKey;
        const hasTestSecurityKey = companyData?.wikiPayitSettings?.securityKey === testSecurityKey;

        // If any of the test keys are present, the account is in test mode
        setIsTestMode(hasTestTokenizationKey || hasTestSecurityKey);
      } catch (error) {
        console.error('Error checking test mode:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkTestMode();
  }, [user?.companyId]);

  return { isTestMode, isLoading };
}; 